import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * Waits for every <nosto-dynamic-card> inside the bag-drawer-recommendations
 * placement to actually finish loading its own content, then lets the
 * `nosto-cards-loading` class (which cart-drawer.liquid uses to keep the
 * list hidden past Nosto's own fixed reveal timer) be removed.
 *
 * Nosto's own campaign template hides its skeleton and reveals the product
 * list on a hardcoded setTimeout that has no way to know how long the
 * individual cards — each fetching its own content independently — actually
 * take. Under real network latency that reveals a list of still-empty
 * cards which then visibly pop in their content afterward. This keeps the
 * (already-visible-by-then) skeleton up instead, until every card is ready,
 * so the swap to the real list happens once, fully populated.
 *
 * @param {Element} root
 */
function revealNostoCardsOnceLoaded(root) {
  const cards = root.querySelectorAll('nosto-dynamic-card');
  if (!cards.length) return;

  const pending = new Set(cards);
  const reveal = () => root.classList.remove('nosto-cards-loading');

  const checkPending = () => {
    pending.forEach((card) => {
      if (card.childElementCount > 0) pending.delete(card);
    });
    return pending.size === 0;
  };

  if (checkPending()) {
    reveal();
    return;
  }

  root.classList.add('nosto-cards-loading');

  const observer = new MutationObserver(() => {
    if (checkPending()) {
      observer.disconnect();
      reveal();
    }
  });
  observer.observe(root, { childList: true, subtree: true });

  // Safety net — don't hide the list forever if a card fails to hydrate.
  setTimeout(() => {
    observer.disconnect();
    reveal();
  }, 5000);
}

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, skip the row-removal animation —
    // updateQuantity will trigger a single view transition to the server-rendered
    // empty state once the fetch returns, showing the complete correct content.
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;
    if (isEmptyCart) return;

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        const sectionHtml = parsedResponseText.sections[this.sectionId];
        const didMorphCartItemsOnly = this.#morphCartItemsOnly(sectionHtml);

        if (!didMorphCartItemsOnly) {
          if (newCartItemCount === 0 && !this.isDrawer) {
            // Single view transition from the live cart to the server-rendered empty
            // state — the callback runs synchronously so the browser captures the
            // correct "after" snapshot containing the complete empty-state HTML
            // (title + buttons) in one step.
            startViewTransition(() => {
              morphSection(this.sectionId, sectionHtml);
              this.#updateCartQuantitySelectorButtonStates();
            }, ['empty-cart-page']);
          } else {
            morphSection(this.sectionId, sectionHtml);
            this.#updateCartQuantitySelectorButtonStates();
          }
        } else {
          this.#updateCartQuantitySelectorButtonStates();
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      const didMorphCartItemsOnly = this.#morphCartItemsOnly(cartItemsHtml);
      if (!didMorphCartItemsOnly) {
        morphSection(this.sectionId, cartItemsHtml);
      }

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }

    // The #bag-drawer-recommendations placement only exists inside the header's
    // cart-drawer markup — the /cart page's own cart-items-component (rendered
    // by sections/main-cart.liquid) has no Nosto slot at all. Both instances are
    // mounted simultaneously when the drawer's header is present on the cart
    // page, and both receive this same document-level CartAddEvent; without this
    // check each one independently re-tags and reloads the same placement,
    // doubling the request and visibly re-rendering it twice.
    if (event instanceof CartAddEvent && this.sectionId === 'cart-drawer' && window.nostojs) {
      // The Nosto app's cart-tagging embed only tags the DOM on full page loads,
      // so an AJAX add-to-cart leaves it stale until the next navigation. Fetch
      // the authoritative cart and push it through setTaggingProvider so Nosto's
      // cart data (used for recommendations, abandoned-cart, retargeting) is
      // current immediately.
      fetch(`${Theme.routes.cart_url}.js`)
        .then((response) => response.json())
        .catch(() => null)
        .then((/** @type {{ items?: Array<{ product_id: number, quantity: number, product_title: string, price: number }> } | null} */ cart) => {
          window.nostojs((api) => {
            if (cart?.items && typeof api.setTaggingProvider === 'function') {
              api.setTaggingProvider('cart', {
                items: cart.items.map((item) => ({
                  product_id: item.product_id.toString(),
                  quantity: item.quantity,
                  name: item.product_title,
                  unit_price: (item.price / 100).toFixed(2),
                  price_currency_code: window.Shopify.currency.active,
                })),
              });
            }

            // Reload ONLY the bag drawer placement rather than every Nosto placement
            // on the page (which is what loadRecommendations() does, plus a page-view
            // per call). Falls back to a full reload if the request-builder API isn't
            // available in the loaded Nosto script.
            //
            // Must go through placements.injectCampaigns (not a manual innerHTML
            // write) — the placement's HTML includes a <script type="module"> that
            // hides its own skeleton and sets up Swiper/lazy-load. Browsers never
            // execute <script> tags inserted via innerHTML, so a manual write would
            // leave the skeleton stuck and Swiper never initialized.
            const { placements } = api;
            if (typeof api.createRecommendationRequest === 'function' && placements) {
              api
                .createRecommendationRequest({ includeTagging: true })
                .setElements(['bag-drawer-recommendations'])
                .setResponseMode('HTML')
                .load()
                .then((/** @type {{ recommendations?: Record<string, unknown> }} */ response) => {
                  if (response?.recommendations) {
                    // The placeholder is rendered WITHOUT the `nosto_element` class
                    // (cart-drawer.liquid) so Nosto's autoloader never requests it —
                    // this explicit setTagging → load → inject sequence is the sole
                    // loader. Add the class back onto just the single target element
                    // (the one injectCampaigns resolves by id) right before injecting,
                    // in case injectCampaigns keys off the class. Injection fills the
                    // element in this same synchronous tick, so even if Nosto's
                    // observer notices the class it skips an already-populated
                    // placement. Never a blanket query — that could re-arm an empty
                    // duplicate.
                    const root = document.getElementById('bag-drawer-recommendations');
                    if (root) root.classList.add('nosto_element');
                    placements.injectCampaigns(response.recommendations);
                    if (root) revealNostoCardsOnceLoaded(root);
                  }
                })
                .catch(() => {
                  // createRecommendationRequest failed — fall back to the blanket
                  // loader. It scans for `.nosto_element`, which the placeholder no
                  // longer carries (autoload is disabled at the source), so restore
                  // the class first or bag-drawer would be skipped by the fallback.
                  const el = document.getElementById('bag-drawer-recommendations');
                  if (el) el.classList.add('nosto_element');
                  api.loadRecommendations();
                });
            } else {
              api.loadRecommendations();
            }
          });
        });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

   /**
   * For cart drawer updates, morph only this cart-items-component to keep
   * the drawer dialog host mounted/open during section refresh.
   *
   * @param {string | undefined} cartItemsHtml
   * @returns {boolean}
   */
  #morphCartItemsOnly(cartItemsHtml) {
    if (this.sectionId !== 'cart-drawer' || !cartItemsHtml) return false;

    const parsed = new DOMParser().parseFromString(cartItemsHtml, 'text/html');
    const nextCartItems = parsed.querySelector(`cart-items-component[data-section-id="${this.sectionId}"]`);

    if (!nextCartItems) return false;

    // Detach the mobile nosto slot before morphing. The morph engine sees server
    // HTML that always has an empty nosto_element, so it would wipe any live
    // Nosto content and destroy the Swiper instance. By detaching first we take
    // the slot entirely out of the morph scope, then put it back ourselves —
    // exactly why the desktop column (which lives outside cart-items-component)
    // never has this problem.
    const nostoMobileBefore = this.querySelector('.cart-drawer__nosto-mobile');
    if (nostoMobileBefore) nostoMobileBefore.remove();

    morph(this, nextCartItems);

    // After the morph: restore the live slot if one existed (preserves Nosto +
    // Swiper); otherwise, on desktop, drop the re-inserted duplicate placement.
    const nostoMobileAfter = this.querySelector('.cart-drawer__nosto-mobile');
    if (nostoMobileBefore && nostoMobileAfter) {
      nostoMobileAfter.replaceWith(nostoMobileBefore);
    } else if (nostoMobileAfter && window.matchMedia('(min-width: 750px)').matches) {
      // Empty→filled morph re-introduced the mobile slot. Autoload is no longer a
      // concern — the placeholders are rendered WITHOUT the `nosto_element` class
      // (cart-drawer.liquid), so Nosto's autoloader never requests them. The only
      // remaining issue is the duplicate `#bag-drawer-recommendations` id: on desktop
      // this same id also lives in the outside column, and two of them would make the
      // explicit injectCampaigns below resolve/populate the wrong (or both) element(s)
      // — the duplicate render, then a re-render once removeWrongSlot() deletes this
      // slot on its 50ms timer. Remove it synchronously here (same outcome as
      // removeWrongSlot, but immediately) so exactly one element carries the id. On
      // mobile this IS the real slot (the outside column was removed at page load), so
      // it's left in place for the handler to inject into.
      nostoMobileAfter.remove();
    }

    // Sync only the cart-drawer--empty class and aria-labelledby on the dialog —
    // these are set by Liquid based on cart.empty? and go stale when morphing
    // only the cart-items-component. Never replace the full className as the
    // dialog picks up runtime classes (dialog-modal, dialog-drawer, open state).
    const dialog = this.closest('dialog');
    const nextDialog = parsed.querySelector('dialog');
    if (dialog && nextDialog) {
      const isEmpty = nextDialog.classList.contains('cart-drawer--empty');
      dialog.classList.toggle('cart-drawer--empty', isEmpty);
      const ariaLabel = nextDialog.getAttribute('aria-labelledby');
      if (ariaLabel) dialog.setAttribute('aria-labelledby', ariaLabel);
    }

    return true;
  }


  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
